import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, CircularProgress, Alert, Divider,
} from '@mui/material';
import SEOHead from '../components/SEO/SEOHead';
import ArticleCard from '../components/Blog/ArticleCard';
import OutfitCard from '../components/Outfit/OutfitCard';
import CategoryService from '../services/category-service';
import BlogService from '../services/blog-service';
import OutfitService from '../services/outfit-service';
import { CategoryMeta, BlogArticle } from '../types/blog';
import { Outfit } from '../types';

const CategoryLandingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<CategoryMeta | null>(null);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const cat = await CategoryService.getCategoryBySlug(slug);
        setCategory(cat);

        // Fetch outfits for this category
        try {
          const outfitData = await OutfitService.getOutfitsByEventType(cat.name, 20);
          setOutfits(outfitData);
        } catch { /* graceful */ }

        // Fetch related blog articles
        try {
          const articleData = await BlogService.listArticles({ limit: 6, status: 'published' });
          setArticles(articleData.articles || []);
        } catch { /* graceful */ }
      } catch {
        setError('Category not found.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !category) return <Alert severity="error" sx={{ m: 3 }}>{error || 'Category not found.'}</Alert>;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.editorialIntro,
    url: `https://hooliah.com/${category.type === 'style' ? 'styles' : 'occasions'}/${category.slug}`,
  };

  return (
    <Box>
      <SEOHead
        title={`${category.name} Fashion`}
        description={category.editorialIntro.slice(0, 160)}
        canonicalUrl={`https://hooliah.com/${category.type === 'style' ? 'styles' : 'occasions'}/${category.slug}`}
        jsonLd={jsonLd}
      />

      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>{category.name}</Typography>
      <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>{category.editorialIntro}</Typography>

      <Divider sx={{ my: 3 }} />

      {/* Outfit feed */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>{category.name} Outfits</Typography>
      {outfits.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 4 }}>No outfits found for this category yet.</Typography>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {outfits.map((outfit) => (
            <Grid item xs={12} sm={6} md={3} key={outfit.id}>
              <OutfitCard outfit={outfit} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Related blog articles */}
      {articles.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Related Articles</Typography>
          <Grid container spacing={3}>
            {articles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.articleId}>
                <ArticleCard article={article} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default CategoryLandingPage;
